import { ApplicationModuleCode } from '@/domain/application-modules/application-module.interface';
import { ApplicationModulesService } from '@/domain/application-modules/application-modules.service';
import type { ProjectId } from '@/interfaces/Project.interface';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import type { Language } from '@/utils/i18n/language.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { inject, Injectable } from '@angular/core';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { combineLatest, type Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import type { UserCore } from '../interfaces/User.interface';
import { RosaAuthService } from '../libs/rosa';
import { BrandingProvider } from '../providers/branding/branding.provider';
import { ProfileProvider } from '../providers/profile/profile.provider';
import { ProjectsProvider } from '../providers/projects/projects.provider';

@Injectable({
    providedIn: 'root'
})
export class TutorialService {
    private _projectsProvider = inject(ProjectsProvider);
    private _profileProvider = inject(ProfileProvider);
    private _rosaAuthService = inject(RosaAuthService);
    private _disabledFeaturesProvider = inject(DisabledFeaturesService);
    private _brandingProvider = inject(BrandingProvider);
    private _applicationModulesProvider = inject(ApplicationModulesService);

    isTutorialEnabled$!: Observable<boolean>;

    async initTutorial() {
        // Intercom
        this.isTutorialEnabled$ = combineLatest([
            this._rosaAuthService.areGeneralConditionAccepted$(),
            this._brandingProvider.hasBrandingAccessToTutorial$(),
            this._applicationModulesProvider.hasModuleAccess$(ApplicationModuleCode.TUTORIAL_DISABLED),
            this._applicationModulesProvider.hasModuleAccess(ApplicationModuleCode.HIDE_TO_ITALY), //TODO : it's not perfect for hiding intercom, we want a method to hide intercom for specific project
            this._rosaAuthService.isConnectedAs$()
        ]).pipe(
            debounceTime(0),
            map(([areGeneralConditionAccepted, hasBrandingAccessToTutorial, tutorialDisabled, hideToItaly, isConnectedAs]) => {
                return (
                    this._disabledFeaturesProvider.isFeatureEnabled(FeatureCode.INTERCOM) &&
                    !tutorialDisabled &&
                    !hideToItaly &&
                    areGeneralConditionAccepted &&
                    hasBrandingAccessToTutorial &&
                    !isConnectedAs
                );
            }),
            distinctUntilChanged(),
            shareReplay({ refCount: true, bufferSize: 1 })
        );

        this.isTutorialEnabled$.subscribe(isEnabled => (isEnabled ? this.enableIntercomHelper() : this.disableIntercomHelper()));

        combineLatest([
            this.isTutorialEnabled$,
            this._profileProvider.getDefinedUser$().pipe(filter(isNotNullish)),
            this._projectsProvider.getUserCurrentProjectId$().pipe(filter(isNotNullish)),
            this._brandingProvider.isCustomBranding$(),
            this._profileProvider.getUserProfileRoles$().pipe(map(profileRoles => profileRoles.map(profileRole => profileRole.roleCode))),
            this._profileProvider.getUserLanguage$()
        ])
            .pipe(debounceTime(1000)) // debounce is here to ensure `this.isTutorialEnabled$` has refresh before sending information to intercom
            .subscribe(([isIntercomEnabled, currentUser, projectId, isNotCustomBranding, roleCodes, language]) => {
                if (isIntercomEnabled) {
                    this.setIntercomAccount(currentUser, projectId, isNotCustomBranding, roleCodes, language);
                }
            });
    }

    private async setIntercomAccount(
        user: UserCore,
        projectId: ProjectId,
        isCustomBranding: boolean,
        userRolesCodes: string[],
        userLanguage: Language
    ) {
        update({
            user_id: user.id,
            name: user.firstname ?? undefined,
            email: user.email,
            currentProjectId: projectId,
            hasAccessToDataboardFeature: userRolesCodes.includes('databoards'),
            hasAccessToAlertFeature: userRolesCodes.includes('alert_role'),
            language_override: userLanguage,
            isProjectAdmin: userRolesCodes.includes('project_admin'),
            customBranding: isCustomBranding
            /*
                /!\ /!\ /!\ NE PAS METTRE LE NUMÉRO DE TÉLÉPHONE / DO NOT SET THE PHONE NUMBER /!\ /!\ /!\

                For RGPD reasons and because intercom store its data in an US server,
                we shall never set the phone number of the client on the data we give to intercom

                For more info, ask Clément B.
            */
        });
    }

    private disableIntercomHelper() {
        console.info('Intercom :: disabled');
        shutdown();
    }

    private enableIntercomHelper() {
        console.info('Intercom :: enabled');
        Intercom({ app_id: 'chsc0o00', region: 'eu' });
    }
}
